import type { CSSProperties } from "react";
import { C, T, W } from "./tokens";

/* Shared visual helpers mirroring the dashboard's card/shadow conventions. */

// Themed (tokens.css): soft navy shadow on light, a visible black halo on dark
// so card elevation reads via shadow in both themes and the stroke stays secondary.
export const SHADOW = {
  rest: "var(--shadow-card-rest)",
  hover: "var(--shadow-card-hover)",
  table: "var(--shadow-card-table)",
  panel: "var(--shadow-card-panel)",
} as const;

export const RADIUS = {
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
} as const;

export const cardBase: CSSProperties = {
  position: "relative",
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: RADIUS.lg,
  boxShadow: SHADOW.rest,
  transition: "box-shadow 300ms ease-out",
};

export const cardXL: CSSProperties = {
  ...cardBase,
  borderRadius: RADIUS.xl,
  border: `1px solid ${C.border}`,
};

// Frosted raised variant for the Intelligence "observatory" shell (design-system
// v2 §A8) — a semi-opaque glass surface over the dark navy ramp, matching
// metric-map's frosted <Card>/.mmap-card so shell sections that compose HeroRow /
// ThreeCharts read as the same product. Swap in for cardBase behind a `frosted`
// prop; on the light theme the near-white tint over the light surface stays legible.
export const frostedCard: CSSProperties = {
  position: "relative",
  // Glass tint via --ds-card-a/b (ds-shell.tsx) so the recipe follows the theme
  // inside a shell; the fallbacks are the previous dark-glass literals verbatim,
  // so any consumer OUTSIDE a shell scope renders exactly as before. Mirrors
  // `frostedCardCss` — the two frosted surfaces stay byte-identical.
  background:
    "linear-gradient(180deg, var(--ds-card-a, rgba(255,255,255,0.055)), var(--ds-card-b, rgba(255,255,255,0.025)))",
  border: `1px solid ${C.border}`,
  borderRadius: RADIUS.xl,
  boxShadow: SHADOW.rest,
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  transition: "box-shadow 300ms ease-out",
};

export const kpiCardPadding: CSSProperties = {
  padding: "36px 28px 36px 32px",
};

/* The `frostedCard` recipe as a CSS declaration string (no trailing padding),
   for sections that style via <style> template strings rather than JSX props —
   the GTM report shells. Append your own padding/margin after it. Keeps the one
   frosted surface identical to the JSX `frostedCard` above. */
export const frostedCardCss = `
  border: 1px solid ${C.border};
  border-radius: ${RADIUS.xl}px;
  /* Glass tint via --ds-card-a/b (ds-shell.tsx) so the recipe follows the theme
     inside a shell. The fallbacks are the previous literals verbatim, so any
     consumer outside a shell scope renders exactly as it did before. */
  background: linear-gradient(
    180deg,
    var(--ds-card-a, rgba(255,255,255,0.055)),
    var(--ds-card-b, rgba(255,255,255,0.025))
  );
  box-shadow: ${SHADOW.rest};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
`;

/* A tint layered over any surface to make it read DEEPER — used on the
   idle/landing-screen preview cards, which felt too light/floaty against the dark
   ramp (design feedback, 2026-07). Now theme-aware via --card-deep-tint: dark keeps
   the original 34% near-black navy (byte-identical); light drops to a near-transparent
   navy so the card stays a clean light surface (border + shadow carry the edge). One
   token so every screen deepens by the same amount and it tunes in one place. */
export const CARD_DEEP_TINT = "var(--card-deep-tint)";

/* The DEEPER frosted surface — {@link frostedCardCss} with {@link CARD_DEEP_TINT}
   layered over the glass, for idle-screen preview cards styled via <style> strings.
   Append your own padding after it. Keeps the deepen identical across screens. */
export const frostedCardDeepCss = `
  border: 1px solid ${C.border};
  border-radius: ${RADIUS.xl}px;
  background:
    ${CARD_DEEP_TINT},
    linear-gradient(
      180deg,
      var(--ds-card-a, rgba(255,255,255,0.055)),
      var(--ds-card-b, rgba(255,255,255,0.025))
    );
  box-shadow: ${SHADOW.rest};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
`;

/* Shape of a `TYPE` ramp entry (tokens.ts). */
export type TypeToken = {
  fontFamily: string;
  fontWeight: number;
  fontSize: number | string;
  letterSpacing?: string;
  lineHeight?: number;
  fontStyle?: string;
};

/* Serialize a TYPE ramp entry into a CSS declaration string, for sections that
   style via <style> template strings while the ramp objects are built for JSX
   spreads. The single shared copy — previously duplicated in five sections.
   Every declaration carries its OWN trailing `;` so callers can append further
   declarations on the next line (e.g. `text-transform: uppercase;`) without the
   two merging into one invalid, dropped declaration. */
export function typeCss(t: TypeToken): string {
  return [
    `font-family: ${t.fontFamily};`,
    `font-weight: ${t.fontWeight};`,
    `font-size: ${typeof t.fontSize === "number" ? `${t.fontSize}px` : t.fontSize};`,
    t.letterSpacing != null ? `letter-spacing: ${t.letterSpacing};` : null,
    t.lineHeight != null ? `line-height: ${t.lineHeight};` : null,
    t.fontStyle != null ? `font-style: ${t.fontStyle};` : null,
  ]
    .filter(Boolean)
    .join(" ");
}

/* Recharts tooltip — matches dashboard's bg-white shadow-lg border rounded-lg.
   Uses the always-opaque tooltipBg (NOT surface): in the observatory shell
   `surface` is a ~4%-opaque glass, which left the tooltip see-through. */
export const chartTooltipContent: CSSProperties = {
  background: C.tooltipBg,
  border: `1px solid ${C.borderStrong}`,
  borderRadius: RADIUS.lg,
  boxShadow: "0 10px 15px -3px rgba(0,0,0,0.35), 0 4px 6px -4px rgba(0,0,0,0.30)",
  padding: "10px 12px",
  fontFamily: "'Noto Sans', sans-serif",
  fontSize: T.cap,
  color: C.textHi,
};

export const chartTooltipLabelStyle: CSSProperties = {
  fontSize: T.micro,
  fontWeight: W.semibold,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: C.textMd,
  marginBottom: 4,
};
